<script lang="ts">
	import { getTranslate } from "../utils/getTranslate";
	import FileUploader from "./Form/FileUploader.svelte";
	import Form from "./Form/Form.svelte";
	import translations from "./CareerForm.translations.json";
	import LabelledInput from "./Form/LabelledInput.svelte";
	import Button from "./Button.svelte";
	import { flyInOnScroll } from "../utils/flyInOnScroll";
	import Checkbox from "./Form/Checkbox.svelte";
	import { mutation } from "../api/graphql.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import type { CareerApplication, CareerApplicationInput } from "../../../core/schema/CareerApplication.js";
	import type { Attachment } from "../../../core/schema/Attachment.js";
	import createCareerApplicationMutation from "../api/mutations/createCareerApplication.graphql";
	import { getTenant } from "../contexts/tenantContextKey.js";
	import Modal from "./Modal.svelte";

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { locale } = getTenant();

	const translate = getTranslate(translations);
	let curriculumVitae: Attachment | undefined;
	let gdprApprovedAt: string | undefined;

	function onGdprCheck(event: Event): void {
		const input = event.target as HTMLInputElement;
		gdprApprovedAt = input.checked ? new Date().toISOString() : undefined;
	}

	let showSuccess = false;
	async function submit(event: SubmitEvent): Promise<void> {
		event.preventDefault();

		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);

		if (!curriculumVitae) {
			throw new Error("Must upload CV");
		}
		if (!gdprApprovedAt) {
			throw new Error("Must approve GDPR");
		}

		await mutation<CareerApplication, { input: CareerApplicationInput }>(
			PUBLIC_GRAPHQL_ENDPOINT,
			createCareerApplicationMutation,
			{
				input: {
					name: String(formData.get("name")),
					surname: String(formData.get("surname")),
					email: String(formData.get("email")),
					phone: String(formData.get("phone")),
					locale,
					message: formData.get("message")?.toString() ?? undefined,
					curriculumVitae,
					gdprApprovedAt,
					fromUrl: window.location.origin + window.location.pathname + window.location.search,
				},
			},
		);
		form.reset();
		showSuccess = true;
		/* eslint-disable require-atomic-updates */
		curriculumVitae = undefined;
		gdprApprovedAt = undefined;
		/* eslint-enable require-atomic-updates */
	}

	/* eslint-disable svelte/no-at-html-tags */
</script>

<Modal bind:show={showSuccess}>
	<p>{translate("thanks")}</p>
	<p>{translate("willBeInTouch")}</p>
</Modal>

<h2
	use:flyInOnScroll
	class="mb-9 pt-8 text-xl font-semibold leading-[1.13] md:text-2xl lg:mx-12 lg:pb-16 lg:pt-32 lg:text-4xl"
>
	{translate("headline")}
</h2>

<Form class="flex w-full flex-col gap-y-3" on:submit={submit}>
	<div class="flex flex-col gap-x-1 gap-y-3 lg:flex-row">
		<div class="w-full" use:flyInOnScroll>
			<LabelledInput id="name" required>{translate("firstName")}</LabelledInput>
		</div>
		<div class="w-full" use:flyInOnScroll>
			<LabelledInput id="surname" required>{translate("lastName")}</LabelledInput>
		</div>
	</div>
	<div class="flex flex-col gap-x-1 gap-y-3 lg:flex-row">
		<div class="w-full" use:flyInOnScroll>
			<LabelledInput inputProps={{ pattern: "[+]?[\\d ]+" }} type="tel" id="phone" required>
				{translate("phone")}
			</LabelledInput>
		</div>
		<div class="w-full" use:flyInOnScroll>
			<LabelledInput id="email" type="email" required>{translate("email")}</LabelledInput>
		</div>
	</div>
	<div use:flyInOnScroll>
		<LabelledInput class="w-full" type="textarea" id="message" placeholder={translate("yourMessage")}>
			{translate("note")}
		</LabelledInput>
	</div>
	<div class="flex flex-col gap-x-1 lg:flex-row lg:justify-between">
		<div class="flex flex-col gap-3">
			<div class="max-w-96" use:flyInOnScroll>
				<FileUploader
					destinationFolder="cv"
					currentFiles={curriculumVitae ? [curriculumVitae] : []}
					on:change={({ detail: { files } }) => {
						// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
						curriculumVitae = files;
					}}
					required
					white
					noBorder
				>
					{translate("uploadCurriculum")}
				</FileUploader>
			</div>
			<div use:flyInOnScroll>
				<Checkbox class="text-md" id="gdpr" required on:change={onGdprCheck}>{translate("understand")}</Checkbox>
			</div>
		</div>

		<div use:flyInOnScroll>
			<Button class="mx-auto mt-4 lg:mx-[initial] lg:mt-0" type="submit" variant="primary">
				{translate("submit")}
			</Button>
		</div>
	</div>
</Form>

<p use:flyInOnScroll class="mt-20 text-lg leading-[1.18] lg:mt-32 lg:leading-[1.56]">
	{@html translate("underFormText")}
</p>
